import { useEffect, useState } from "react";

import axios from "axios";

import {
  defaultApiUrl,
  API_VERSION,
} from "../../../../libraries/network.implementation";

export interface ReceiptUploadConfig {
  allowed_mime_types: string[];
  max_size_bytes: number;
}

export type ReceiptUploadConfigStatus = "idle" | "pending" | "ready" | "error";

interface UseReceiptUploadConfigResult {
  status: ReceiptUploadConfigStatus;
  config: ReceiptUploadConfig | null;
}

const CONFIG_ENDPOINT = `${defaultApiUrl}/${API_VERSION}/receipts/config`;

let cachedConfig: ReceiptUploadConfig | null = null;
let inFlightPromise: Promise<ReceiptUploadConfig> | null = null;

const fetchConfig = (): Promise<ReceiptUploadConfig> => {
  if (cachedConfig) return Promise.resolve(cachedConfig);
  if (inFlightPromise) return inFlightPromise;

  inFlightPromise = axios
    .get<ReceiptUploadConfig>(CONFIG_ENDPOINT, { withCredentials: true })
    .then((response) => {
      cachedConfig = response.data;
      return response.data;
    })
    .finally(() => {
      inFlightPromise = null;
    });

  return inFlightPromise;
};

export const resetReceiptUploadConfigCacheForTests = () => {
  cachedConfig = null;
  inFlightPromise = null;
};

const useReceiptUploadConfig = (
  enabled: boolean
): UseReceiptUploadConfigResult => {
  const [config, setConfig] = useState<ReceiptUploadConfig | null>(
    () => cachedConfig
  );
  const [status, setStatus] = useState<ReceiptUploadConfigStatus>(() =>
    cachedConfig ? "ready" : "idle"
  );

  useEffect(() => {
    if (!enabled) {
      setStatus((prev) => (prev === "error" ? "idle" : prev));
      return undefined;
    }
    if (cachedConfig) {
      setConfig(cachedConfig);
      setStatus("ready");
      return undefined;
    }

    let cancelled = false;
    setStatus("pending");

    fetchConfig()
      .then((data) => {
        if (cancelled) return;
        setConfig(data);
        setStatus("ready");
      })
      .catch(() => {
        if (cancelled) return;
        setConfig(null);
        setStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { status, config };
};

export default useReceiptUploadConfig;
